import { DocRenderer } from '@cyntler/react-doc-viewer';
import { ClickAwayListener } from '@material-ui/core';
import { throttle } from 'lodash';
import React, { useContext, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import { ClickAwayContext } from './context';
import { PreviewControls } from './PreviewControls';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const MIN_ZOOM = 1;
const MAX_ZOOM = 2;

export const PdfRenderer: DocRenderer = (props) => {
  const [activePage, setActivePage] = React.useState(1);
  const [zoom, setZoom] = React.useState(1);
  const { onClickAway } = useContext(ClickAwayContext);
  const [totalPages, setTotalPages] = React.useState<number | null>(null);
  const pagesRefs = React.useRef<Array<React.RefObject<HTMLDivElement>>>([]);

  // Inject annotation layer CSS (can't use import due to isomorphic-style-loader)
  useEffect(() => {
    const styleId = 'pdf-annotation-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .react-pdf__Page {
          position: relative;
        }
        .react-pdf__Page__annotations.annotationLayer {
          position: absolute;
          left: 0;
          top: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          opacity: 1;
          line-height: 1.0;
          pointer-events: auto;
        }
        .annotationLayer section {
          position: absolute;
          text-align: initial;
        }
        .annotationLayer .textWidgetAnnotation input,
        .annotationLayer .textWidgetAnnotation textarea,
        .annotationLayer .choiceWidgetAnnotation select,
        .annotationLayer .buttonWidgetAnnotation.checkBox input,
        .annotationLayer .buttonWidgetAnnotation.radioButton input {
          background-image: var(--annotation-unfocused-field-background);
          border: 1px solid transparent;
          box-sizing: border-box;
          font-size: 9px;
          height: 100%;
          margin: 0;
          padding: 0 3px;
          vertical-align: top;
          width: 100%;
        }
        .annotationLayer .buttonWidgetAnnotation.radioButton input {
          border-radius: 50%;
        }
        .annotationLayer .textWidgetAnnotation textarea {
          font: message-box;
          font-size: 9px;
          resize: none;
        }
        .annotationLayer .buttonWidgetAnnotation.checkBox input,
        .annotationLayer .buttonWidgetAnnotation.radioButton input {
          appearance: none;
          padding: 0;
          position: relative;
        }
        .annotationLayer .buttonWidgetAnnotation.checkBox input:checked:before,
        .annotationLayer .buttonWidgetAnnotation.checkBox input:checked:after,
        .annotationLayer .buttonWidgetAnnotation.radioButton input:checked:before {
          background-color: rgba(0, 0, 0, 1);
          content: '';
          display: block;
          position: absolute;
        }
        .annotationLayer .buttonWidgetAnnotation.checkBox input:checked:before,
        .annotationLayer .buttonWidgetAnnotation.checkBox input:checked:after {
          height: 80%;
          left: 45%;
          width: 1px;
        }
        .annotationLayer .buttonWidgetAnnotation.checkBox input:checked:before {
          transform: rotate(45deg);
        }
        .annotationLayer .buttonWidgetAnnotation.checkBox input:checked:after {
          transform: rotate(-45deg);
        }
        .annotationLayer .buttonWidgetAnnotation.radioButton input:checked:before {
          border-radius: 50%;
          height: 50%;
          left: 30%;
          top: 20%;
          width: 50%;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const handlePageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let page = parseInt(e.target.value);
    // Scroll to the specified page if it exists
    if (totalPages) {
      page = Math.max(1, Math.min(page, totalPages));
    }
    setActivePage(page);
    const targetPage = pagesRefs.current[page - 1]?.current;
    if (targetPage) {
      targetPage.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start',
      });
    }
  };
  const handleZoomIn = () => {
    if (zoom < MAX_ZOOM) {
      setZoom(zoom + 0.1);
    }
  };

  const handleZoomOut = () => {
    if (zoom > MIN_ZOOM) {
      setZoom(zoom - 0.1);
    }
  };

  const [pdfPageWidth, setPdfPageWidth] = React.useState(0);
  const pdfWrapper = React.useRef<HTMLDivElement>(null);

  /**
   * In order to keep the pdf page width responsive we need to set the width of the pdf page
   * to the width of the parent container. This function handles the pdf page width resizing.
   */
  const setPdfSize = () => {
    if (pdfWrapper && pdfWrapper.current) {
      setPdfPageWidth(pdfWrapper.current.getBoundingClientRect().width);
    }
  };

  React.useEffect(() => {
    window.addEventListener('resize', throttle(setPdfSize, 3000));
    setPdfSize();
    return () => {
      window.removeEventListener('resize', throttle(setPdfSize, 3000));
    };
  }, []);

  const observerCallback = React.useCallback(
    (entries: Array<IntersectionObserverEntry>) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const pageIndex = Number((entry.target as HTMLElement).dataset.index);
          setActivePage(pageIndex + 1); // Pages are 1-based
        }
      });
    },
    [],
  );

  React.useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    });

    pagesRefs.current.reverse().forEach((ref) => {
      if (ref && ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [totalPages, observerCallback]);

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <div
        className="flex justify-center h-full pt-16 md:w-1/2 mx-auto absolute inset-0"
        ref={pdfWrapper}
      >
        {totalPages && (
          <PreviewControls
            pages={totalPages}
            activePage={activePage}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            resetZoom={() => setZoom(1)}
            onPageChange={handlePageChange}
          />
        )}
        <Document
          file={props.mainState.currentDocument?.fileData}
          onLoadSuccess={({ numPages }: { numPages: number }) => {
            setTotalPages(numPages);
            // init pages refs
            pagesRefs.current = Array(numPages)
              .fill(null)
              .map(() => React.createRef());
          }}
        >
          {Array.from(new Array(totalPages || 0), (_1, index) => {
            return (
              <div
                key={index}
                ref={pagesRefs.current[index]}
                data-index={index}
                className="mb-4"
              >
                <Page
                  pageNumber={index + 1}
                  scale={zoom}
                  width={pdfPageWidth}
                  renderAnnotationLayer={true}
                  renderForms={true}
                />
              </div>
            );
          })}
        </Document>
      </div>
    </ClickAwayListener>
  );
};

PdfRenderer.fileTypes = ['pdf', 'application/pdf'];
PdfRenderer.weight = 1;
